/**
 * GuestPrivacyCard — plain-language "what we do with your info" summary.
 *
 * Shown on the /privacy page and embedded on the /join/{slug} public page
 * so real guests (not just operators reading legal text) can quickly see:
 *   1. What we store
 *   2. Who sees it
 *   3. How long we keep it
 *   4. What we NEVER ask for
 *   5. How to opt out / delete
 *
 * All numbers/behaviors here MUST stay in sync with:
 *   - /app/backend/core.py :: redact_old_pii (90-day sweep)
 *   - /app/backend/core.py :: scrub_pii     (at-ingest PII scrubbing)
 *   - /app/backend/routes/legal.py          (Privacy Policy source of truth)
 */
import { ShieldCheck, ClockCounterClockwise, Prohibit, Trash, Eye } from "@phosphor-icons/react";

const ROWS = [
  { icon: ShieldCheck, title: "Only what's needed", body: "We store your name, mobile phone, and party size — that's it. Never a card number, birthdate, or ID." },
  { icon: Eye, title: "Just the restaurant sees it", body: "Your info is scoped to this restaurant's dashboard. BuzzerAI doesn't sell, share, or advertise on it." },
  { icon: ClockCounterClockwise, title: "Auto-deletes after 90 days", body: "Phone numbers are scrambled and call transcripts are wiped 90 days after the visit — automatically, no request needed." },
  { icon: Prohibit, title: "The AI refuses sensitive info", body: "If you accidentally start reading a card or DOB to our receptionist, the AI politely stops you and drops it from the transcript before saving." },
  { icon: Trash, title: "You control it", body: "Text STOP to any SMS to opt out instantly. Ask the restaurant to delete your record and it's gone within 24 hours." },
];

export default function GuestPrivacyCard({ compact = false }) {
  return (
    <div
      className={`paper-card ${compact ? "p-4" : "p-6"} bg-[#C2D1C1]/25 border-2 border-[#1E3A2F]/15`}
      data-testid="guest-privacy-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#1E3A2F] flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={16} color="#F5C543" weight="fill" />
        </div>
        <div>
          <div className="overline text-[#1E3A2F]">Your privacy at a glance</div>
          <div className={`font-heading font-extrabold ${compact ? "text-sm" : "text-base"} text-[#121914]`}>
            What we do — and don't — with your info
          </div>
        </div>
      </div>
      <div className={`grid ${compact ? "grid-cols-1 gap-2" : "sm:grid-cols-2 gap-3"}`}>
        {ROWS.map(({ icon: Icon, title, body }) => (
          <div key={title} className="flex items-start gap-2.5" data-testid={`privacy-row-${title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
            <Icon size={16} weight="fill" color="#1E3A2F" className="mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-[#121914] leading-snug">{title}</div>
              <div className="text-[11px] text-[#5C6B61] leading-snug mt-0.5">{body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
